import {
	Button,
	Chip,
	ChipSelect,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Input,
	Label,
	ResourceOptions,
	Textarea,
} from "@ondit/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Check, MoreHorizontal, Plus } from "lucide-react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { toast } from "sonner";
import { MarketingPlan } from "~/common/marketing/marketing";
import { useAccess } from "~/core/access/index";
import type { PlanAction, PlanCommand, PlanDetail } from "~/core/api/content-plans";
import { ApiError, api } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Hint } from "~/core/ui/page";
import { SearchSelect } from "~/core/ui/search-select";
import { Async, Empty } from "~/core/ui/states";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";
import { readingPath } from "~/modules/analytics/lib/loop";

export function ContentPlansPage(props: ReadingProps) {
	const { entityId } = props;
	const t = useT();
	const { locale } = useI18n();
	const { editor } = useAccess();
	const [params, setParams] = useSearchParams();
	const id = params.get("plan");
	const creating = params.get("new") === "1";
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "content-plans"],
		queryFn: () => api.contentPlans(entityId),
		...read,
	});
	const [search, setSearch] = useState("");
	function show(id?: string) {
		setParams(id ? { plan: id } : {});
		void state.refetch();
	}
	return (
		<StageShell
			{...props}
			reading="plans"
			lede={t("plans.lede")}
			actions={
				!id && !creating ? (
					<Chip variant="primary" leftIcon={Plus} disabled={!editor} onClick={() => setParams({ new: "1" })}>
						{t("plans.new")}
					</Chip>
				) : undefined
			}
		>
			{creating ? (
				<PlanForm
					entityId={entityId}
					locale={locale}
					gapId={params.get("gap") ?? undefined}
					pageId={params.get("page") ?? undefined}
					initialTitle={params.get("title") ?? ""}
					onCreated={show}
					onCancel={() => show()}
				/>
			) : id ? (
				<PlanWorkspace key={`${entityId}:${id}`} {...props} id={id} onBack={() => show()} />
			) : (
				<Async state={state}>
					{({ plans }) =>
						plans.length === 0 ? (
							<Empty title={t("plans.empty")} body={t("plans.emptyBody")} />
						) : (
							<section className="content-plan-list">
								<ResourceOptions
									translate={t}
									className="analytics-table-tools"
									search={{ value: search, onChange: setSearch, placeholder: t("plans.search") }}
								/>
								{plans
									.filter((p) => `${p.title} ${p.objective}`.toLowerCase().includes(search.toLowerCase()))
									.map((plan) => (
										<article key={plan.id} className="content-plan-row">
											<div>
												<span className="eyebrow">{t(`plans.${plan.status}`)}</span>
												<h2>
													<Button
														variant="link"
														type="button"
														className="content-plan-title h-auto p-0 whitespace-normal text-left justify-start"
														onClick={() => show(plan.id)}
													>
														{plan.title}
													</Button>
												</h2>
												{plan.objective.trim() !== plan.title.trim() ? <p>{plan.objective}</p> : null}
												<span className="hint">
													{t("plans.progress", { done: plan.done_count ?? 0, total: plan.action_count ?? 0 })}
												</span>
											</div>
											<Button
												variant="ghost"
												onClick={() => show(plan.id)}
												aria-label={`${t("plans.open")}: ${plan.title}`}
											>
												<ArrowRight />
											</Button>
										</article>
									))}
							</section>
						)
					}
				</Async>
			)}
		</StageShell>
	);
}

function PlanForm({
	entityId,
	locale,
	gapId,
	pageId,
	initialTitle,
	onCreated,
	onCancel,
}: {
	entityId: string;
	locale: string;
	gapId?: string;
	pageId?: string;
	initialTitle: string;
	onCreated: (id: string) => void;
	onCancel: () => void;
}) {
	const t = useT();
	const { editor } = useAccess();
	const { locale: uiLocale } = useI18n();
	const [title, setTitle] = useState(initialTitle);
	const [objective, setObjective] = useState(initialTitle);
	const [audience, setAudience] = useState("");
	const [language, setLanguage] = useState(locale);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const queryClient = useQueryClient();
	const create = useMutation({
		mutationFn: (body: Parameters<typeof api.createContentPlan>[1]) => api.createContentPlan(entityId, body),
		onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) }),
	});
	return (
		<form
			className="content-plan-form"
			onSubmit={async (e) => {
				e.preventDefault();
				if (busy) return;
				setBusy(true);
				setError("");
				try {
					onCreated(
						(
							await create.mutateAsync({
								title,
								objective,
								audience,
								locale: language,
								gap_id: gapId,
								page_id: pageId,
							})
						).id,
					);
				} catch (e) {
					setError(e instanceof ApiError ? e.localized(uiLocale) : String(e));
				} finally {
					setBusy(false);
				}
			}}
		>
			<Button type="button" variant="ghost" onClick={onCancel}>
				<ArrowLeft />
				{t("plans.back")}
			</Button>
			<h2>{t("plans.new")}</h2>
			<p className="hint">{t("plans.createHelp")}</p>
			<fieldset disabled={!editor || busy} className="stack bare-fieldset">
				<Label htmlFor="plan-title">{t("plans.title")}</Label>
				<Input
					id="plan-title"
					required
					maxLength={200}
					value={title}
					placeholder={t("plans.titlePlaceholder")}
					onChange={(e) => setTitle(e.target.value)}
				/>
				<Label htmlFor="plan-objective">{t("plans.objective")}</Label>
				<Textarea
					id="plan-objective"
					required
					maxLength={4000}
					rows={4}
					value={objective}
					placeholder={t("plans.objectivePlaceholder")}
					onChange={(e) => setObjective(e.target.value)}
				/>
				<Label htmlFor="plan-audience">{t("plans.audience")}</Label>
				<Input
					id="plan-audience"
					maxLength={1000}
					value={audience}
					placeholder={t("plans.audiencePlaceholder")}
					onChange={(e) => setAudience(e.target.value)}
				/>
				<Label htmlFor="plan-language">{t("plans.language")}</Label>
				<ChipSelect
					value={language}
					onChange={setLanguage}
					id="plan-language"
					options={[
						{ value: "en", label: t("locale.en") },
						{ value: "zh", label: t("locale.zh") },
					]}
				/>
				{error && (
					<p role="alert" className="error">
						{error}
					</p>
				)}
				<Button type="submit" disabled={!title.trim() || !objective.trim()}>
					{t("plans.new")}
				</Button>
			</fieldset>
		</form>
	);
}

function PlanWorkspace(props: ReadingProps & { id: string; onBack: () => void }) {
	const state = useQuery({
		queryKey: [...keys.entity(props.entityId), "content-plan", props.id],
		queryFn: () => api.contentPlan(props.entityId, props.id),
		...read,
		// Asked again while one of its actions is running, so the step it
		// reaches shows without a reload.
		refetchInterval: (query) =>
			query.state.data?.actions.some((a) => ["pending", "running", "queued"].includes(a.job_status ?? ""))
				? 5000
				: false,
	});
	return (
		<Async state={state}>{(data) => <PlanContent {...props} data={data} reload={() => void state.refetch()} />}</Async>
	);
}
function PlanContent({
	entityId,
	id,
	onBack,
	data,
	reload,
	filters,
}: ReadingProps & { id: string; onBack: () => void; data: PlanDetail; reload: () => void }) {
	const t = useT();
	const { locale } = useI18n();
	const { editor } = useAccess();
	const [params] = useSearchParams();
	const [selected, setSelected] = useState<string | null>(params.get("action"));
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const [adding, setAdding] = useState(false);
	const queryClient = useQueryClient();
	const send = useMutation({
		mutationFn: (body: Parameters<typeof api.planCommand>[2]) => api.planCommand(entityId, id, body),
		onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) }),
	});
	const action =
		data.actions.find((a) => a.id === selected) ??
		data.actions.find((a) => !["done", "cancelled"].includes(a.status)) ??
		data.actions[0];
	const active = data.plan.status === "active";
	async function command(command: Omit<PlanCommand, "expected_revision" | "request_id">) {
		if (busy) return false;
		setBusy(true);
		setError("");
		try {
			await send.mutateAsync({
				...command,
				expected_revision: data.plan.revision,
				request_id: crypto.randomUUID(),
			});
			toast.success(t("plans.saved"));
			return true;
		} catch (e) {
			setError(e instanceof ApiError ? e.localized(locale) : String(e));
			return false;
		} finally {
			setBusy(false);
		}
	}
	const remaining = data.actions.filter((a) => !["done", "cancelled"].includes(a.status));
	return (
		<div className="content-plan-workspace">
			<Button variant="ghost" onClick={onBack}>
				<ArrowLeft />
				{t("plans.back")}
			</Button>
			<header className="content-plan-heading">
				<div>
					<span className="eyebrow">{t(`plans.${data.plan.status}`)}</span>
					<h2>{data.plan.title}</h2>
					<p>{data.plan.objective}</p>
					{data.plan.audience && (
						<p className="hint">
							{t("plans.audience")}: {data.plan.audience}
						</p>
					)}
				</div>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant="ghost" disabled={!editor || busy} aria-label={t("plans.actions")}>
							<MoreHorizontal />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuItem onClick={() => command({ operation: active ? "pause" : "resume" })}>
							{t(active ? "plans.pause" : "plans.resume")}
						</DropdownMenuItem>
						<DropdownMenuItem
							disabled={!active || remaining.length > 0}
							onClick={() => command({ operation: "complete" })}
						>
							{t("plans.close")}
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</header>
			{error && (
				<div role="alert" className="error">
					{error}
					<Button variant="ghost" onClick={reload}>
						{t("state.retry")}
					</Button>
				</div>
			)}
			{!active && (
				<p role="status" className="hint">
					{t(data.plan.status === "paused" ? "plans.pauseHelp" : "plans.completed")}
				</p>
			)}
			<MarketingPlan brandId={entityId} planId={data.plan.id} />
			<div className="content-plan-next">
				<span className="eyebrow">{t("marketing.manualNext")}</span>
				<p>{remaining[0]?.title ?? t("plans.actionsReady")}</p>
			</div>
			<div className="content-plan-grid">
				<section className="content-plan-actions" aria-label={t("plans.actions")}>
					{data.actions.map((item, i) => (
						<Button
							variant="ghost"
							type="button"
							key={item.id}
							onClick={() => {
								setSelected(item.id);
								setAdding(false);
							}}
							className="content-plan-action h-auto items-start justify-start whitespace-normal px-(--s-8) py-(--s-8) gap-(--s-8) aria-[current=step]:bg-surface-hover"
							aria-current={action?.id === item.id ? "step" : undefined}
						>
							<span className="content-plan-number">
								{item.status === "done" ? <Check /> : String(i + 1).padStart(2, "0")}
							</span>
							<span>
								<strong>{item.title}</strong>
								<span className="hint">
									{t(`plans.${item.status}`)} · {t(`plans.${item.destination}`)}
								</span>
							</span>
						</Button>
					))}
					<Button variant="ghost" disabled={!editor || !active || busy} onClick={() => setAdding(!adding)}>
						<Plus />
						{t("plans.add")}
					</Button>
				</section>
				<section className="content-plan-detail">
					{adding ? (
						<AddAction
							busy={busy}
							onAdd={async (body) => {
								if (await command({ operation: "add", ...body })) setAdding(false);
							}}
						/>
					) : action ? (
						<ActionDetail
							key={action.id}
							action={action}
							entityId={entityId}
							planId={id}
							filters={{ ...filters, locale: data.plan.locale }}
							disabled={!editor || !active || busy}
							onCommand={command}
						/>
					) : (
						<p>{t("plans.selectAction")}</p>
					)}
				</section>
			</div>
			<section className="content-plan-evidence">
				<h3>{t("publishing.title")}</h3>
				{data.publications?.length ? (
					data.publications.map((p) => (
						<p key={p.id}>
							<Link to={`${readingPath(`/w/${entityId}`, "distribution")}?tab=history`}>{p.title}</Link> · {p.published}
							/{p.deliveries} {t("publishing.confirmed")}
							{p.attention > 0 && <> · {t("todo.publishing_attention.title", { n: p.attention })}</>}
						</p>
					))
				) : (
					<p className="hint">{t("publishing.empty")}</p>
				)}
				<Link to={`${readingPath(`/w/${entityId}`, "distribution")}`}>{t("publishing.title")}</Link>
			</section>
			<section className="content-plan-evidence">
				<h3>{t("plans.evidence")}</h3>
				{data.evidence.length ? (
					data.evidence.map((e) => (
						<p key={e.id}>
							{e.kind === "page" ? (
								<Link to={`/w/${entityId}/pages/${e.source_id}`}>{e.label}</Link>
							) : (
								<Link to={readingPath(`/w/${entityId}`, "gaps")}>{e.label}</Link>
							)}
						</p>
					))
				) : (
					<p className="hint">{t("plans.noEvidence")}</p>
				)}
			</section>
		</div>
	);
}
function ActionDetail({
	action,
	entityId,
	planId,
	filters,
	disabled,
	onCommand,
}: {
	action: PlanAction;
	entityId: string;
	planId: string;
	filters: ReadingProps["filters"];
	disabled: boolean;
	onCommand: (command: Omit<PlanCommand, "expected_revision" | "request_id">) => Promise<boolean>;
}) {
	const t = useT();
	const { locale } = useI18n();
	const [note, setNote] = useState(action.note);
	const [pageId, setPageId] = useState("");
	// Only the titles are read here, so only the language is asked for: the
	// same list the publishing screens hold.
	const pages = useQuery({
		queryKey: [...keys.entity(entityId), "pages", filters.locale],
		queryFn: () => api.pages(entityId, { locale: filters.locale }),
		...read,
	});
	const base = `/w/${entityId}`;
	const destination =
		action.kind === "facts"
			? `${base}/settings`
			: action.kind === "research"
				? readingPath(base, "visibility")
				: action.kind === "observe"
					? readingPath(base, "reflows")
					: action.kind === "distribute"
						? `${readingPath(base, "distribution")}?plan=${planId}${action.page_id ? `&page=${action.page_id}` : ""}`
						: action.page_id
							? `${base}/pages/${action.page_id}`
							: `${base}/compose?plan=${planId}&action=${action.id}&locale=${filters.locale}`;
	return (
		<>
			<span className="eyebrow">{t(`plans.${action.kind}`)}</span>
			<h3>{action.title}</h3>
			<Button asChild variant="default">
				<Link to={destination}>
					{t(`plans.${action.kind}`)}
					<ArrowRight />
				</Link>
			</Button>
			{action.next_at && (
				<p>
					{t("plans.runAt")}: {fmt.dateTime(locale, action.next_at)}
				</p>
			)}
			{action.job_status && (
				<p role="status" className="hint">
					{t("plans.execution")}: {t(`tasks.${action.job_status}`)}
				</p>
			)}
			{["failed", "skipped", "cancelled"].includes(action.job_status ?? "") && (
				<Button
					disabled={disabled}
					variant="default"
					onClick={() => onCommand({ operation: "retry", action_id: action.id })}
				>
					{t("plans.prepareRetry")}
				</Button>
			)}
			{pages.error && (
				<p role="alert" className="error">
					{t("plans.articleLoadFailed")}
				</p>
			)}
			{action.page_title && (
				<p>
					<Link to={`${base}/pages/${action.page_id}`}>{action.page_title}</Link>
				</p>
			)}
			<fieldset disabled={disabled} className="stack bare-fieldset">
				{["create", "update"].includes(action.kind) && (
					<>
						<Label>{t("plans.linkArticle")}</Label>
						<SearchSelect
							value={pageId}
							onChange={setPageId}
							label={t("plans.linkArticle")}
							placeholder={t("plans.selectArticle")}
							searchLabel={t("plans.selectArticle")}
							emptyLabel={t("table.noMatch")}
							disabled={disabled || pages.isFetching}
							options={(pages.data ?? []).map((p) => ({ value: p.id, label: p.title }))}
						/>
						<Button
							variant="default"
							disabled={!pageId}
							onClick={() => onCommand({ operation: "link", action_id: action.id, page_id: pageId })}
						>
							{t("plans.link")}
						</Button>
					</>
				)}
				<Label htmlFor="plan-outcome">
					{t("plans.outcome")} <Hint text={t("plans.outcomeHint")} />
				</Label>
				<Textarea id="plan-outcome" rows={5} value={note} onChange={(e) => setNote(e.target.value)} maxLength={10000} />
				<div className="content-plan-buttons">
					<Button variant="default" onClick={() => onCommand({ operation: "note", action_id: action.id, note })}>
						{t("plans.saveNote")}
					</Button>
					<Button
						disabled={action.status === "running" || (action.status !== "done" && !note.trim())}
						onClick={() =>
							onCommand({ operation: action.status === "done" ? "reopen" : "done", action_id: action.id, note })
						}
					>
						{t(action.status === "done" ? "plans.reopen" : "plans.finish")}
					</Button>
				</div>
			</fieldset>
		</>
	);
}
function AddAction({
	busy,
	onAdd,
}: {
	busy: boolean;
	onAdd: (body: { title: string; kind: string; destination: string }) => void;
}) {
	const t = useT();
	const [title, setTitle] = useState("");
	const [kind, setKind] = useState("create");
	const [destination, setDestination] = useState("ondit");
	return (
		<form
			onSubmit={(e) => {
				e.preventDefault();
				onAdd({ title, kind, destination });
			}}
		>
			<fieldset disabled={busy} className="stack bare-fieldset">
				<h3>{t("plans.add")}</h3>
				<Label htmlFor="action-title">{t("plans.title")}</Label>
				<Input id="action-title" value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={200} />
				<Label>{t("plans.kind")}</Label>
				<ChipSelect
					value={kind}
					onChange={setKind}
					options={["research", "facts", "create", "update", "distribute", "observe"].map((k) => ({
						value: k,
						label: t(`plans.${k}`),
					}))}
				/>
				<Label>{t("plans.destination")}</Label>
				<ChipSelect
					value={destination}
					onChange={setDestination}
					options={["ondit", "website", "community", "social"].map((k) => ({ value: k, label: t(`plans.${k}`) }))}
				/>
				<Button type="submit" disabled={!title.trim()}>
					{t("plans.add")}
				</Button>
			</fieldset>
		</form>
	);
}
